import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  IconButton,
  Box,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import type { CatalogueCustomSection } from '@traaker/shared';

/** The fixed, always-available sections — differ by page type. */
const DEFAULT_PRODUCT_SECTIONS: { key: string; label: string }[] = [
  { key: 'usp', label: 'Points forts (USP)' },
  { key: 'description', label: 'Description' },
  { key: 'specs', label: 'Spécifications & chiffres' },
  { key: 'shopperIntent', label: "Intention d'achat / usages" },
  { key: 'competitiveness', label: 'Compétitivité' },
  { key: 'faq', label: 'FAQ' },
];
const DEFAULT_CATEGORY_SECTIONS: { key: string; label: string }[] = [
  { key: 'topParagraph', label: 'Paragraphe haut de page' },
  { key: 'bottomParagraph', label: 'Paragraphe bas de page' },
];

/**
 * Configure a catalogue's wording sections (item b): toggle the FIXED default
 * sections on/off, and manage user-defined CUSTOM sections beyond them. Each
 * custom section has a label + an instruction telling the model what to write,
 * and a "generated by default" toggle. Saved on the catalogue; applied to the
 * next optimize run. Max 10 custom sections (enforced server-side too).
 */
export function CustomSectionsDialog({
  open,
  initial,
  initialDisabledSections,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: CatalogueCustomSection[];
  /** Fixed sections currently disabled for this catalogue (item b). */
  initialDisabledSections?: string[];
  onClose: () => void;
  onSave: (sections: CatalogueCustomSection[], disabledSections: string[]) => Promise<void>;
}) {
  const [rows, setRows] = useState<CatalogueCustomSection[]>(() =>
    initial.length ? initial.map((s) => ({ ...s })) : [],
  );
  const [disabled, setDisabled] = useState<Set<string>>(
    () => new Set(initialDisabledSections || []),
  );
  const [saving, setSaving] = useState(false);

  const update = (i: number, patch: Partial<CatalogueCustomSection>) =>
    setRows((r) => r.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const remove = (i: number) => setRows((r) => r.filter((_, k) => k !== i));
  const add = () =>
    setRows((r) => [...r, { key: '', label: '', instruction: '', enabledByDefault: true }]);
  const toggleDefault = (key: string) =>
    setDisabled((d) => {
      const next = new Set(d);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Configurer</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Sections par défaut
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Désactivez une section standard que vous ne voulez jamais générer (ex. une marketplace
          qui ne veut pas de « Compétitivité »). S'applique à la prochaine analyse.
        </Typography>
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          {[...DEFAULT_PRODUCT_SECTIONS, ...DEFAULT_CATEGORY_SECTIONS].map((s) => (
            <FormControlLabel
              key={s.key}
              control={
                <Switch
                  size="small"
                  checked={!disabled.has(s.key)}
                  onChange={() => toggleDefault(s.key)}
                />
              }
              label={<Typography variant="body2">{s.label}</Typography>}
            />
          ))}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Sections personnalisées
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Ajoutez des sections de réécriture propres à ce catalogue (ex. « Livraison »,
          « Garantie », « Éco-responsabilité »). Elles seront générées lors de la prochaine
          analyse, en plus des sections standard.
        </Alert>
        <Stack spacing={2}>
          {rows.map((s, i) => (
            <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  value={s.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                  label="Nom de la section"
                  size="small"
                  fullWidth
                  placeholder="ex. Livraison & retours"
                />
                <IconButton size="small" onClick={() => remove(i)} aria-label="Supprimer">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              <TextField
                value={s.instruction || ''}
                onChange={(e) => update(i, { instruction: e.target.value })}
                label="Consigne pour l'IA (que doit contenir cette section)"
                size="small"
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 1 }}
                placeholder="ex. Résume les options de livraison, délais et politique de retour, à partir de la page."
              />
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={
                  <Switch
                    size="small"
                    checked={s.enabledByDefault !== false}
                    onChange={(e) => update(i, { enabledByDefault: e.target.checked })}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    Générer par défaut
                  </Typography>
                }
              />
            </Box>
          ))}
          <Button startIcon={<AddIcon />} onClick={add} disabled={rows.length >= 10} sx={{ alignSelf: 'flex-start' }}>
            Ajouter une section
          </Button>
          {rows.length >= 10 && (
            <Typography variant="caption" color="text.secondary">
              Maximum 10 sections personnalisées.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Annuler
        </Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              // Drop empty rows (no label) before saving.
              await onSave(rows.filter((s) => s.label.trim()), [...disabled]);
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? <CircularProgress size={20} /> : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
